/**
 * TemplatesTab — the study's reusable composer reply templates.
 *
 * Two surfaces (mirroring PrescreenTab / TextArtifactPanel):
 *   - The LIVE templates list: the active crm.message_templates rows for this
 *     study (what shows in the composer's "Templates" dropdown). Each is
 *     editable/removable inline; "+ Add template" creates a manual one. Open to
 *     any CRM user (recruiters can save their own); edit/remove is creator-or-
 *     admin (server-enforced).
 *   - The admin AI workspace: generate a starter set from the protocol/ICF
 *     (a 'message_templates' artifact), review the draft, then publish — publish
 *     replaces the study's AI template set and they become live + editable above.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Sparkles, Plus, Pencil, Trash2 } from 'lucide-react';
import api from '../../../services/api';
import { MessageTemplate, StudyDetailResponse } from '../../types';
import { relTime, studyHasExtractedDoc } from '../../lib';
import { useConfirm } from '../ui';
import { workingArtifact } from './TextArtifactPanel';
import TemplateForm, { ChannelChip, CategoryChip, TemplateChannel, TemplateCategory, TemplateDraft } from './TemplateForm';

type Channel = TemplateChannel;
type DraftValue = TemplateDraft;

export default function TemplatesTab({ detail, isAdmin, onChanged }: { detail: StudyDetailResponse; isAdmin: boolean; onChanged: () => void }) {
  const studyId = detail.study.id;
  const confirm = useConfirm();
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const working = workingArtifact(detail.artifacts, 'message_templates');
  const hasExtractedDoc = studyHasExtractedDoc(detail);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await api.get(`/api/crm/studies/${studyId}/templates`);
      setTemplates(res.data?.templates || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [studyId]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
    } catch (err: any) {
      setError(err?.response?.data?.error || `${label} failed`);
    } finally {
      setBusy(null);
    }
  };

  const create = (v: DraftValue) => run('Save', async () => {
    await api.post(`/api/crm/studies/${studyId}/templates`, v);
    setAdding(false);
    await loadTemplates();
    onChanged();
  });
  const update = (id: string, v: DraftValue) => run('Save', async () => {
    await api.patch(`/api/crm/templates/${id}`, v);
    setEditingId(null);
    await loadTemplates();
    onChanged();
  });
  const remove = async (id: string) => {
    if (!(await confirm({
      title: 'Remove this template?',
      message: 'Recruiters will no longer see it in the composer.',
      confirmLabel: 'Remove',
      danger: true,
    }))) return;
    run('Remove', async () => { await api.delete(`/api/crm/templates/${id}`); await loadTemplates(); onChanged(); });
  };

  const generate = async () => {
    if (working?.status === 'draft' && !(await confirm({
      title: 'Discard the current AI draft and generate a new set from the protocol?',
      confirmLabel: 'Discard and regenerate',
      danger: true,
    }))) return;
    run('Generate', () => api.post(`/api/crm/studies/${studyId}/generate`, { types: ['message_templates'] }).then(onChanged));
  };
  const publishDraft = () => run('Publish', async () => {
    await api.post(`/api/crm/artifacts/${working!.id}/publish`, {});
    await loadTemplates();
    onChanged();
  });
  // Set the AI draft set aside so it stops re-appearing; the live templates above
  // stay as-is. The AI only comes back on an explicit Regenerate.
  const discardDraft = async () => {
    if (working!.status === 'draft'
        && !(await confirm({
          title: 'Dismiss this AI draft set?',
          message: 'Your live templates above stay as-is.',
          confirmLabel: 'Dismiss draft',
          danger: true,
        }))) return;
    run('Discard', async () => { await api.post(`/api/crm/artifacts/${working!.id}/discard`, {}); onChanged(); });
  };

  const draftTemplates: DraftValue[] = Array.isArray(working?.content?.templates) ? working!.content.templates : [];

  const generateBtn = (label: string) => (
    <button
      type="button"
      onClick={generate}
      disabled={busy !== null || !hasExtractedDoc || working?.status === 'generating'}
      title={!hasExtractedDoc ? 'Upload a protocol/ICF in Documents first' : undefined}
      className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Sparkles className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div className="space-y-4">
      {/* ---- Live templates (the composer dropdown set) ---- */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Message templates</h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Quick replies recruiters pick from the composer's Templates button for this study.
            </p>
          </div>
          {!adding && (
            <button
              type="button"
              onClick={() => { setAdding(true); setEditingId(null); }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <Plus className="h-4 w-4" /> Add template
            </button>
          )}
        </div>

        {error && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

        {adding && (
          <div className="mb-3">
            <TemplateForm busy={busy === 'Save'} onSave={create} onCancel={() => setAdding(false)} />
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : templates.length === 0 && !adding ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No templates yet. {isAdmin ? 'Generate a starter set with AI below, or add your own.' : 'Add one with “Add template”.'}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {templates.map((t) => (
              <li key={t.id} className="py-3">
                {editingId === t.id ? (
                  <TemplateForm
                    initial={{ channel: t.channel, category: t.category, title: t.title, subject: t.subject || '', body: t.body }}
                    busy={busy === 'Save'}
                    onSave={(v) => update(t.id, v)}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.title}</span>
                        <ChannelChip channel={t.channel} />
                        <CategoryChip category={t.category} />
                        {t.source === 'ai' && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                            <Sparkles className="h-3 w-3" /> AI
                          </span>
                        )}
                      </div>
                      {t.subject && <p className="mt-1 text-xs font-medium text-gray-600 dark:text-gray-300">Subject: {t.subject}</p>}
                      <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-600 dark:text-gray-400">{t.body}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => { setEditingId(t.id); setAdding(false); }}
                        aria-label="Edit template"
                        className="rounded p-1.5 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(t.id)}
                        aria-label="Remove template"
                        className="rounded p-1.5 text-gray-400 transition-colors duration-150 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ---- AI starter-set workspace (admin) ---- */}
      {isAdmin && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Draft a starter set with AI</h3>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                Reads the protocol/ICF and drafts common recruiter replies. Review them, then publish — published templates become live above.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {working && working.status !== 'generating' && (
                <button
                  type="button"
                  onClick={discardDraft}
                  disabled={busy !== null}
                  title="Dismiss this AI draft and keep your live templates"
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {busy === 'Discard' ? 'Dismissing…' : 'Dismiss'}
                </button>
              )}
              {(!working || working.status !== 'generating') && generateBtn(working?.status === 'draft' || templates.some((t) => t.source === 'ai') ? 'Regenerate' : 'Generate with AI')}
            </div>
          </div>

          {working?.status === 'generating' && (
            <div className="flex items-center gap-3 rounded-lg bg-primary-50 p-4 dark:bg-primary-900/20">
              <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
              <p className="text-sm text-primary-800 dark:text-primary-200">Reading the documents and drafting message templates…</p>
            </div>
          )}
          {working?.status === 'failed' && (
            <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
              Draft failed: {working.error || 'unknown error'}
            </p>
          )}
          {working?.status === 'draft' && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                  {draftTemplates.length} drafted — review before publishing
                </span>
                <button
                  type="button"
                  onClick={publishDraft}
                  disabled={busy !== null}
                  className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50"
                >
                  {busy === 'Publish' ? 'Publishing…' : 'Publish these templates'}
                </button>
              </div>
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
                {draftTemplates.map((t, i) => (
                  <li key={i} className="p-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.title}</span>
                      <ChannelChip channel={(t.channel as Channel) || 'any'} />
                      <CategoryChip category={(t.category as TemplateCategory) || 'study_details'} />
                    </div>
                    {t.subject && <p className="mt-1 text-xs font-medium text-gray-600 dark:text-gray-300">Subject: {t.subject}</p>}
                    <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-600 dark:text-gray-400">{t.body}</p>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Publishing replaces the current AI templates for this study; your manually-added templates are kept. You can fine-tune each one above after publishing.
                {working.generated_at ? ` · Drafted ${relTime(working.generated_at)} ago` : ''}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
